import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from "react";
import { tr } from "@/lib/tr";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import { UNSAFE_NavigationContext as NavigationContext, useLocation, useNavigate } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useTranslation } from "react-i18next";

/**
 * Global "dirty form" guard.
 *
 * Pages call `useUnsavedChangesWarning(isDirty)` to register an unsaved-changes
 * state.  Any in-app navigation (router push/replace), browser back/forward,
 * or the global Back button surfaces a professional AlertDialog asking the
 * user to confirm before discarding their work.  Closing/refreshing the tab
 * triggers the native beforeunload prompt.
 */
type Resolver = (ok: boolean) => void;

interface DirtyNavCtx {
  setDirty: (key: string, dirty: "add" | "edit" | false) => void;
  isDirty: () => boolean;
  /** True when any registration is in "add" (new document) mode. */
  isAddMode: () => boolean;
  /** Confirm-then-execute. Resolves true if user agreed to discard. */
  confirm: () => Promise<boolean>;
  /** Smart back: confirms if dirty, then navigates back (or to fallback). */
  goBack: (fallback?: string) => Promise<void>;
  /** Synchronously clear all dirty flags. Call right before navigating
   *  after a successful save so the guard does not fire on the post-save
   *  redirect (React state updates are async; the guard reads a ref). */
  markClean: () => void;
}

const Ctx = createContext<DirtyNavCtx | null>(null);

export const useDirtyNav = () => {
  const c = useContext(Ctx);
  if (!c) throw new Error("useDirtyNav must be used inside <DirtyNavProvider>");
  return c;
};

export const DirtyNavProvider = ({ children }: { children: ReactNode }) => {
  const { i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  const dirtyMap = useRef<Map<string, "add" | "edit" | false>>(new Map());
  const [open, setOpen] = useState(false);
  const resolverRef = useRef<Resolver | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { navigator } = useContext(NavigationContext as any) as { navigator: any };

  const isDirty = useCallback(() => {
    for (const v of dirtyMap.current.values()) if (v) return true;
    return false;
  }, []);

  const isAddMode = useCallback(() => {
    for (const v of dirtyMap.current.values()) if (v === "add") return true;
    return false;
  }, []);

  const setDirty = useCallback((key: string, dirty: "add" | "edit" | false) => {
    if (dirty) dirtyMap.current.set(key, dirty);
    else dirtyMap.current.delete(key);
  }, []);

  const confirm = useCallback((): Promise<boolean> => {
    if (!isDirty()) return Promise.resolve(true);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
      setOpen(true);
    });
  }, [isDirty]);

  // Clear dirty flags whenever the route actually changes (clean-exit guarantee)
  useEffect(() => {
    dirtyMap.current.clear();
  }, [location.pathname]);

  // Beforeunload (close / refresh tab)
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!isDirty()) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  // Patch react-router navigator to intercept push/replace
  useEffect(() => {
    if (!navigator) return;
    const origPush = navigator.push;
    const origReplace = navigator.replace;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    navigator.push = (...args: any[]) => {
      if (!isDirty()) return origPush.apply(navigator, args);
      confirm().then((ok) => { if (ok) { dirtyMap.current.clear(); origPush.apply(navigator, args); } });
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    navigator.replace = (...args: any[]) => {
      if (!isDirty()) return origReplace.apply(navigator, args);
      confirm().then((ok) => { if (ok) { dirtyMap.current.clear(); origReplace.apply(navigator, args); } });
    };
    return () => {
      navigator.push = origPush;
      navigator.replace = origReplace;
    };
  }, [navigator, isDirty, confirm]);

  // Intercept browser back/forward
  useEffect(() => {
    const handler = (e: PopStateEvent) => {
      if (!isDirty()) return;
      // Re-push the current entry to cancel the back action while we ask
      window.history.pushState(null, "", window.location.href);
      confirm().then((ok) => {
        if (ok) {
          dirtyMap.current.clear();
          window.history.back();
        }
      });
    };
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, [isDirty, confirm]);

  const goBack = useCallback(async (fallback?: string) => {
    const ok = await confirm();
    if (!ok) return;
    dirtyMap.current.clear();
    // Always land on the module list page (or dashboard) rather than an
    // unrelated previous history entry.
    navigate(fallback ?? "/dashboard");
  }, [confirm, navigate]);

  const handleAnswer = (ok: boolean) => {
    setOpen(false);
    const r = resolverRef.current;
    resolverRef.current = null;
    r?.(ok);
  };

  const markClean = useCallback(() => { dirtyMap.current.clear(); }, []);

  const value = useMemo<DirtyNavCtx>(() => ({ setDirty, isDirty, isAddMode, confirm, goBack, markClean }), [setDirty, isDirty, isAddMode, confirm, goBack, markClean]);

  const addModeAtOpen = open && isAddMode();

  return (
    <Ctx.Provider value={value}>
      {children}
      <AlertDialog open={open} onOpenChange={(o) => { if (!o) handleAnswer(false); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {addModeAtOpen
                ? tr("تحذير: لم يتم حفظ المستند", "Warning: Document not saved", "Avertissement : document non enregistré")
                : tr("تنبيه: تغييرات غير محفوظة", "Unsaved changes", "Changements non enregistrés")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {addModeAtOpen
                ? tr(
                    "هذا المستند لم يُحفظ بعد. الخروج الآن سيؤدي إلى ضياع المستند بالكامل ولا يمكن استرداده.",
                    "This document has not been saved yet. Leaving now will permanently discard it.",
                    "Ce document n'a pas encore été enregistré. Quitter maintenant le supprimera définitivement.",
                  )
                : tr(
                    "لديك تغييرات غير محفوظة. الخروج الآن سيؤدي إلى فقدانها نهائياً.",
                    "You have unsaved changes. Leaving now will permanently discard them.",
                    "Vous avez des modifications non enregistrées qui seront définitivement perdues.",
                  )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => handleAnswer(false)}>
              {tr("البقاء للتعديل", "Stay & keep editing", "Rester et modifier")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleAnswer(true)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {tr("الخروج دون حفظ", "Discard & exit", "Annuler et quitter")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Ctx.Provider>
  );
};
